import React, { useEffect, useState, useRef } from 'react';
import { GlobeIcon, TwitterIcon, InstagramIcon, YoutubeIcon, LinkedinIcon, XIcon, Loader2Icon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
export const StoreSettingsSection = () => {
  const {
    user
  } = useAuth();
  // State management
  const [storeData, setStoreData] = useState({
    storeName: '',
    storeSlug: '',
    storeDescription: '',
    storeLogo: '',
    storeBanner: '',
    socialLinks: {
      website: '',
      twitter: '',
      instagram: '',
      youtube: '',
      tiktok: '',
      linkedin: ''
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...storeData
  });
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Fetch store data from Firestore
  useEffect(() => {
    const fetchStoreData = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const storeDocRef = doc(db, 'stores', user.id);
        const storeDoc = await getDoc(storeDocRef);
        if (storeDoc.exists()) {
          const data = storeDoc.data();
          // Initialize store data from Firestore
          const storeSettings = {
            storeName: data.storeName || '',
            storeSlug: data.storeSlug || '',
            storeDescription: data.storeDescription || '',
            storeLogo: data.storeLogo || '',
            storeBanner: data.storeBanner || '',
            socialLinks: {
              website: data.socialLinks?.website || '',
              twitter: data.socialLinks?.twitter || '',
              instagram: data.socialLinks?.instagram || '',
              youtube: data.socialLinks?.youtube || '',
              tiktok: data.socialLinks?.tiktok || '',
              linkedin: data.socialLinks?.linkedin || ''
            }
          };
          setStoreData(storeSettings);
          setFormData(storeSettings);
        } else {
          // Create a new store document if it doesn't exist
          const userDocRef = doc(db, 'users', user.id);
          const userDoc = await getDoc(userDocRef);
          let userName = '';
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userName = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          }
          // Generate slug from name
          const storeSlug = (userName || 'mystore').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 30);
          const newStoreData = {
            storeName: userName || 'My Store',
            storeSlug,
            storeDescription: '',
            storeLogo: '',
            storeBanner: '',
            socialLinks: {
              website: '',
              twitter: '',
              instagram: '',
              youtube: '',
              tiktok: '',
              linkedin: ''
            },
            createdAt: new Date(),
            ownerId: user.id
          };
          await setDoc(storeDocRef, newStoreData);
          setStoreData(newStoreData);
          setFormData(newStoreData);
        }
      } catch (err) {
        console.error('Error fetching store data:', err);
        setError('Failed to load store data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStoreData();
  }, [user]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name.includes('.')) {
      // Handle nested objects like socialLinks.website
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: value
        }
      }));
    } else {
      // Handle direct properties
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Auto-generate slug from store name if it's the store name field
      if (name === 'storeName') {
        const slug = value.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 30);
        setFormData(prev => ({
          ...prev,
          storeSlug: slug
        }));
      }
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const storeDocRef = doc(db, 'stores', user.id);
      // Prepare data for Firestore
      const updatedData = {
        storeName: formData.storeName,
        storeSlug: formData.storeSlug,
        storeDescription: formData.storeDescription,
        storeLogo: formData.storeLogo,
        storeBanner: formData.storeBanner,
        socialLinks: formData.socialLinks,
        updatedAt: new Date()
      };
      // Update Firestore document
      await updateDoc(storeDocRef, updatedData);
      // Update local state
      setStoreData({
        ...formData
      });
      setSuccessMessage('Store settings updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating store settings:', err);
      setError('Failed to update store settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  // Close menus when clicking outside
  const handleClickOutside = () => {
    // No longer needed but kept for consistency
  };
  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setFormData({
      ...storeData
    });
    setError(null);
    setSuccessMessage(null);
    setIsEditing(false);
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3621">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3622" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3623">
          Loading store settings...
        </span>
      </div>;
  }
  return <div onClick={handleClickOutside} data-id="element-3624">
      <div className="flex justify-between items-center mb-6" data-id="element-3625">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3626">
          Store Settings
        </h2>
        <button onClick={e => {
        e.stopPropagation();
        setIsEditing(!isEditing);
        if (isEditing) {
          handleCancelEdit();
        }
      }} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" disabled={isSaving} data-id="element-3627">
          {isEditing ? 'Cancel' : 'Edit Store'}
        </button>
      </div>
      {/* Success message */}
      {successMessage && <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-3628">
          <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3629" />
          <span data-id="element-3630">{successMessage}</span>
        </div>}
      {/* Error message */}
      {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-3631">
          <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3632" />
          <span data-id="element-3633">{error}</span>
        </div>}
      <form onSubmit={handleSubmit} data-id="element-3634">
        <div className="mb-8" data-id="element-3635">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3636">
            {/* Store Name */}
            <div data-id="element-3637">
              <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3638">
                Store Name <span className="text-red-500" data-id="element-3639">*</span>
              </label>
              {isEditing ? <input type="text" id="storeName" name="storeName" value={formData.storeName} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3640" /> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3641">
                  {storeData.storeName || '—'}
                </p>}
              <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3642">
                Your public-facing store or brand name
              </p>
            </div>
            {/* Store URL/Slug */}
            <div data-id="element-3643">
              <label htmlFor="storeSlug" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3644">
                Store URL
              </label>
              <div className="flex items-center" data-id="element-3645">
                <span className="text-gray-500 dark:text-[#7A8F8A] mr-1" data-id="element-3646">
                  your-domain.com/seller/
                </span>
                <p className="text-gray-900 dark:text-[#F8FAF9] font-medium" data-id="element-3647">
                  {formData.storeSlug || '—'}
                </p>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3648">
                Auto-generated from your store name
              </p>
            </div>
            {/* Store Description */}
            <div className="md:col-span-2" data-id="element-3649">
              <label htmlFor="storeDescription" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3650">
                Store Description
              </label>
              {isEditing ? <textarea id="storeDescription" name="storeDescription" value={formData.storeDescription} onChange={handleInputChange} rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3651" /> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3652">
                  {storeData.storeDescription || '—'}
                </p>}
              <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3653">
                1-2 paragraph description of what your store offers
              </p>
            </div>
            {/* Social Media Links */}
            <div className="md:col-span-2" data-id="element-3654">
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-3655">
                Social Media Links
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3656">
                {/* Website */}
                <div data-id="element-3657">
                  <label htmlFor="socialLinks.website" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3658">
                    Website
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3659">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3660">
                        <GlobeIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3661" />
                      </div>
                      <input type="url" id="socialLinks.website" name="socialLinks.website" value={formData.socialLinks.website} onChange={handleInputChange} placeholder="https://yourwebsite.com" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3662" />
                    </div> : <div className="flex items-center" data-id="element-3663">
                      <GlobeIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3664" />
                      {storeData.socialLinks.website ? <a href={storeData.socialLinks.website} target="_blank" rel="noopener noreferrer" className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-3665">
                          {storeData.socialLinks.website}
                        </a> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3666">—</p>}
                    </div>}
                </div>
                {/* X (Twitter) */}
                <div data-id="element-3667">
                  <label htmlFor="socialLinks.twitter" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3668">
                    X (Twitter)
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3669">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3670">
                        <TwitterIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3671" />
                      </div>
                      <input type="text" id="socialLinks.twitter" name="socialLinks.twitter" value={formData.socialLinks.twitter} onChange={handleInputChange} placeholder="username" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3672" />
                    </div> : <div className="flex items-center" data-id="element-3673">
                      <TwitterIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3674" />
                      {storeData.socialLinks.twitter ? <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3675">
                          @{storeData.socialLinks.twitter}
                        </p> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3676">—</p>}
                    </div>}
                </div>
                {/* Instagram */}
                <div data-id="element-3677">
                  <label htmlFor="socialLinks.instagram" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3678">
                    Instagram
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3679">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3680">
                        <InstagramIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3681" />
                      </div>
                      <input type="text" id="socialLinks.instagram" name="socialLinks.instagram" value={formData.socialLinks.instagram} onChange={handleInputChange} placeholder="username" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3682" />
                    </div> : <div className="flex items-center" data-id="element-3683">
                      <InstagramIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3684" />
                      {storeData.socialLinks.instagram ? <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3685">
                          {storeData.socialLinks.instagram}
                        </p> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3686">—</p>}
                    </div>}
                </div>
                {/* YouTube */}
                <div data-id="element-3687">
                  <label htmlFor="socialLinks.youtube" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3688">
                    YouTube
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3689">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3690">
                        <YoutubeIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3691" />
                      </div>
                      <input type="text" id="socialLinks.youtube" name="socialLinks.youtube" value={formData.socialLinks.youtube} onChange={handleInputChange} placeholder="channel name" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3692" />
                    </div> : <div className="flex items-center" data-id="element-3693">
                      <YoutubeIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3694" />
                      {storeData.socialLinks.youtube ? <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3695">
                          {storeData.socialLinks.youtube}
                        </p> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3696">—</p>}
                    </div>}
                </div>
                {/* TikTok */}
                <div data-id="element-3697">
                  <label htmlFor="socialLinks.tiktok" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3698">
                    TikTok
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3699">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3700">
                        <XIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3701" />
                      </div>
                      <input type="text" id="socialLinks.tiktok" name="socialLinks.tiktok" value={formData.socialLinks.tiktok} onChange={handleInputChange} placeholder="username" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3702" />
                    </div> : <div className="flex items-center" data-id="element-3703">
                      <XIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3704" />
                      {storeData.socialLinks.tiktok ? <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3705">
                          @{storeData.socialLinks.tiktok}
                        </p> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3706">—</p>}
                    </div>}
                </div>
                {/* LinkedIn */}
                <div data-id="element-3707">
                  <label htmlFor="socialLinks.linkedin" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3708">
                    LinkedIn
                  </label>
                  {isEditing ? <div className="relative" data-id="element-3709">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-3710">
                        <LinkedinIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3711" />
                      </div>
                      <input type="text" id="socialLinks.linkedin" name="socialLinks.linkedin" value={formData.socialLinks.linkedin} onChange={handleInputChange} placeholder="username or company name" className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3712" />
                    </div> : <div className="flex items-center" data-id="element-3713">
                      <LinkedinIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-3714" />
                      {storeData.socialLinks.linkedin ? <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3715">
                          {storeData.socialLinks.linkedin}
                        </p> : <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3716">—</p>}
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isEditing && <div className="flex justify-end" data-id="element-3717">
            <button type="button" onClick={handleCancelEdit} className="px-5 py-2.5 mr-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-all duration-200" disabled={isSaving} data-id="element-3718">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isSaving} data-id="element-3719">
              {isSaving ? <>
                  <Loader2Icon className="h-4 w-4 mr-2 animate-spin" data-id="element-3720" />
                  Saving...
                </> : 'Save Changes'}
            </button>
          </div>}
      </form>
    </div>;
};